import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../../../services/user/user.service';
import { AuthService } from '../../../services/auth/auth.service';
import { environment } from '../../../environment/environment';
import { Subject } from 'rxjs';
import { takeUntil, take, filter } from 'rxjs/operators';
import { DunaUserCateringPreferencesComponent } from '../duna-user-catering-preferences/duna-user-catering-preferences.component';
import { DunaUserNotificationPreferencesComponent } from '../duna-user-notification-preferences/duna-user-notification-preferences.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TokenStateService } from '../../../services/token-state/token-state.service';
import { PushNotificationService } from '../../../services/push-notification/push-notification.service';
import {
  DunaDeviceDebugDialogComponent,
  DunaDeviceDebugDialogData,
} from './duna-device-debug-dialog.component';

@Component({
  selector: 'app-duna-user-settings',
  standalone: true,
  imports: [
    MatButtonModule,
    MatInputModule,
    MatSnackBarModule,
    MatDialogModule,
    DunaUserCateringPreferencesComponent,
    DunaUserNotificationPreferencesComponent,
  ],
  templateUrl: './duna-user-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-user-settings.component.scss'],
})
export class DunaUserSettingsComponent implements OnInit, OnDestroy {
  userName = '';
  calendarUrl = '';
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private tokenStateService: TokenStateService,
    private pushNotificationService: PushNotificationService
  ) {}

  private getApiBaseUrl(): string {
    let base = (environment.apiUrl || '').replace(/\/$/, '');
    if (base && !/^https?:\/\//i.test(base)) {
      base = 'https://' + base;
    }
    return base;
  }

  ngOnInit(): void {
    // Get current user UUID from token: wait for auth ready, then read user synchronously from AuthService
    this.authService.authStateReady$
      .pipe(
        filter((ready) => ready),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const tokenUser = this.authService.getCurrentUser();
        const uuid = tokenUser?.uuid;
        if (uuid) {
          const base = this.getApiBaseUrl();
          const fallbackUrl = base ? base + '/ics/online/' + uuid : '';
          this.calendarUrl = fallbackUrl;

          // Preferred: ask API for the absolute URL (handles proxies / base paths)
          if (base) {
            this.http
              .get<{ url?: string }>(`${base}/ics/online/url`)
              .pipe(take(1), takeUntil(this.destroy$))
              .subscribe({
                next: (res) => {
                  if (res?.url) this.calendarUrl = res.url;
                },
                error: () => {
                  // keep fallbackUrl
                },
              });
          }

          this.userService
            .getUserByUuid(uuid)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (user) => {
                this.userName =
                  [user.first_name, user.last_name]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || 'Bruger';
              },
            });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  copyLink(): void {
    if (!this.calendarUrl) return;
    navigator.clipboard.writeText(this.calendarUrl).then(
      () => this.snackBar.open('Link kopieret', undefined, { duration: 2000 }),
      () =>
        this.snackBar.open('Kunne ikke kopiere link', undefined, {
          duration: 3000,
        })
    );
  }

  async openDeviceDebug(): Promise<void> {
    const deviceIdentifier =
      localStorage.getItem('device_identifier') ||
      this.tokenStateService.getDeviceId() ||
      '—';

    let pushToken = await this.tokenStateService.getToken();
    if (!pushToken) {
      pushToken = await this.pushNotificationService.getFcmToken();
    }
    const pushTokenDisplay = pushToken || '—';
    const platform = this.pushNotificationService.getPlatform();

    const base = this.getApiBaseUrl();
    if (base) {
      this.http
        .post(
          `${base}/users/log-device-debug`,
          {
            device_identifier: deviceIdentifier === '—' ? '' : deviceIdentifier,
            push_token: pushTokenDisplay === '—' ? '' : pushTokenDisplay,
            platform,
          },
          { withCredentials: true }
        )
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({ error: () => {} });
    }

    const dialogData: DunaDeviceDebugDialogData = {
      deviceIdentifier,
      pushToken: pushTokenDisplay,
    };

    this.dialog.open(DunaDeviceDebugDialogComponent, {
      data: dialogData,
      width: 'min(420px, 92vw)',
      autoFocus: false,
    });
  }
}
